import React, { useRef } from 'react';
import { map, max, min, isUndefined, isEmpty, isNaN, keys, values, zip, sum, first, eq, isNumber } from 'lodash';
import PropTypes from 'prop-types';
import Moment from 'moment';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { chartContextButtonInit, contextButtonVisualSettings } from './chartSettings';

require('highcharts-exporting')(Highcharts);
require('highcharts-export-csv')(Highcharts);
require('highcharts-no-data-to-display')(Highcharts);
chartContextButtonInit();

export default function SingleTargetChart({
  advancedMetric = '',
  advancedMetricChartType,
  advancedMetricTargetUnit = '',
  advancedMetricType = '',
  height = null,
  measureResults,
  metricType = 'Image',
  showCSVDownload,
  singleTargetAggregationFunction,
  singleTargetMilestoneDate: originSingleTargetMilestoneDate = '',
  singleTargetTargetAmount: originSingleTargetTargetAmount = '',
  startOfFinancialYear,
  title,
  viewType,
}) {
  const highchartsRef = useRef(null);

  let singleTargetMilestoneDate = originSingleTargetMilestoneDate;
  let seriesData = measureResults
    .filter(({ dataPointX, dataPointYValue }) => Moment(dataPointX).isValid() && isNumber(parseFloat(dataPointYValue)))
    .map(({ dataPointX, dataPointYValue }) => [
      isNaN(Moment(dataPointX).valueOf()) ? 0 : Moment.utc(dataPointX).valueOf(),
      isNaN(parseFloat(dataPointYValue)) ? 0 : parseFloat(dataPointYValue),
    ]);
  let aggResult;
  const dataPointYValues = measureResults.map(({ dataPointYValue }) => parseFloat(dataPointYValue));

  if (singleTargetAggregationFunction === 'sum' || singleTargetAggregationFunction === 'average') {
    const reducer = (accumulator, currentValue) => accumulator + currentValue;

    aggResult = map(measureResults, ({ dataPointX }, index) => {
      const aggValue = dataPointYValues.slice(0, index + 1).reduce(reducer);
      const aggSumOrAverage = Number(
        Number((eq(singleTargetAggregationFunction, 'sum') ? aggValue : aggValue / (index + 1)) || 0).toFixed(2),
      );

      return [isNaN(Moment(dataPointX).valueOf()) ? 0 : Moment.utc(dataPointX).valueOf(), aggSumOrAverage];
    });
  }

  const dataPointX = measureResults.map((measureResult) => Moment.utc(measureResult.dataPointX).valueOf());

  singleTargetMilestoneDate = singleTargetMilestoneDate ? Moment(singleTargetMilestoneDate).valueOf() : null;

  const singleTargetTargetAmount = originSingleTargetTargetAmount;
  const dataPointXValues = [...(singleTargetMilestoneDate !== null ? [singleTargetMilestoneDate] : []), ...dataPointX];
  const xAxisMax = max(dataPointXValues);
  const xAxisMin = min(dataPointXValues);
  let xAxis = {
    type: 'datetime',
    max: isEmpty(seriesData) ? undefined : xAxisMax,
    min: isEmpty(seriesData) ? undefined : xAxisMin,
    plotLines: isEmpty(seriesData)
      ? []
      : [
          {
            color: '#FF0000',
            width: singleTargetMilestoneDate ? 2 : 0,
            value: singleTargetMilestoneDate,
            zIndex: 7,
          },
        ],
  };
  const averageFunc = (acc, value) => {
    const result = (sum(acc.prev) + value) / (acc.prev.length + 1);

    return {
      results: [...acc.results, result],
      prev: [...acc.prev, value],
    };
  };
  const sumFunc = (acc, value) => {
    const result = sum([...acc.prev, value]);

    return {
      results: [...acc.results, result],
      prev: [...acc.prev, value],
    };
  };

  if (dataPointX.length <= 1) {
    xAxis.labels = { format: '{value:%e. %b}' };
  }

  let yAxisMax = max([
    ...seriesData.map((d) => parseFloat(d[1])),
    ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d[1]))]),
    ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
  ]);
  const chartType = advancedMetricChartType === 'bar' ? 'column' : advancedMetricChartType;
  const sumSeriesData = {};
  const avgSeriesData = {};
  let aggSumSeriesData = [];
  let aggAvgSeriesData = [];
  const countSeriesData = {};
  let averages = {};
  let sums = {};

  if (
    (eq(metricType, 'OriginalSingle') || (eq(advancedMetric, 'Yes') && eq(advancedMetricType, 'single_target'))) &&
    singleTargetAggregationFunction !== 'none'
  ) {
    const fiscalYearStartMonth = Moment.utc(startOfFinancialYear).month() + 1;

    switch (viewType) {
      case 'weekly':
        seriesData.forEach((d) => {
          const key = Moment.utc(first(d)).startOf('isoweek').valueOf();

          sumSeriesData[key] = sumSeriesData[key] ? sumSeriesData[key] + d[1] : d[1];
          countSeriesData[key] = countSeriesData[key] ? countSeriesData[key] + 1 : 1;
          if (avgSeriesData[key]) {
            avgSeriesData[key] = (avgSeriesData[key] + d[1]) / countSeriesData[key];
          } else {
            [, avgSeriesData[key]] = d;
          }
        });
        values(countSeriesData).reduce((acc, cur, index) => {
          countSeriesData[keys(countSeriesData)[index]] = acc + cur;

          return acc + cur;
        }, 0);
        aggSumSeriesData.push(first(values(sumSeriesData)));
        values(sumSeriesData).reduce((acc, cur) => {
          aggSumSeriesData.push(acc + cur);

          return acc + cur;
        });
        values(avgSeriesData).reduce((acc, cur, index) => {
          aggAvgSeriesData.push((acc + cur) / values(countSeriesData)[index]);

          return acc + cur;
        }, 0);

        if (singleTargetAggregationFunction === 'sum') {
          seriesData = zip(keys(sumSeriesData), values(sumSeriesData));
          aggResult = zip(keys(sumSeriesData), aggSumSeriesData);
        } else if (singleTargetAggregationFunction === 'average') {
          seriesData = zip(keys(avgSeriesData), values(avgSeriesData));
          aggResult = zip(keys(avgSeriesData), aggAvgSeriesData);
        }

        seriesData = seriesData.map((d) => [parseInt(d[0], 10), Math.round(d[1] * 100) / 100]);
        aggResult = aggResult.map((d) => [parseInt(d[0], 10), Math.round(d[1] * 100) / 100]);
        yAxisMax = max([
          ...seriesData.map((d) => parseFloat(d[1])),
          ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d[1]))]),
          ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
        ]);
        break;
      case 'monthly':
        seriesData.forEach((d) => {
          const key = Moment.utc(first(d)).startOf('month').valueOf();

          sumSeriesData[key] = sumSeriesData[key] ? sumSeriesData[key] + d[1] : d[1];
          countSeriesData[key] = countSeriesData[key] ? countSeriesData[key] + 1 : 1;
          avgSeriesData[key] = avgSeriesData[key] ? (avgSeriesData[key] + d[1]) / 2 : d[1];
        });
        values(countSeriesData).reduce((acc, cur, index) => {
          countSeriesData[keys(countSeriesData)[index]] = acc + cur;

          return acc + cur;
        }, 0);
        aggSumSeriesData.push(first(values(sumSeriesData)));
        values(sumSeriesData).reduce((acc, cur) => {
          aggSumSeriesData.push(acc + cur);

          return acc + cur;
        });
        values(avgSeriesData).reduce((acc, cur, index) => {
          aggAvgSeriesData.push((acc + cur) / values(countSeriesData)[index]);

          return acc + cur;
        }, 0);

        if (singleTargetAggregationFunction === 'sum') {
          seriesData = zip(keys(sumSeriesData), values(sumSeriesData));
          aggResult = zip(keys(sumSeriesData), aggSumSeriesData);
        } else if (singleTargetAggregationFunction === 'average') {
          seriesData = zip(keys(avgSeriesData), values(avgSeriesData));
          aggResult = zip(keys(avgSeriesData), aggAvgSeriesData);
        }

        seriesData = seriesData.map((d) => [parseInt(d[0], 10), Math.round(d[1] * 100) / 100]);
        aggResult = aggResult.map((d) => [parseInt(d[0], 10), Math.round(d[1] * 100) / 100]);
        yAxisMax = max([
          ...seriesData.map((d) => parseFloat(d[1])),
          ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d[1]))]),
          ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
        ]);
        break;
      case 'fiscalQuarter':
        xAxis = { categories: [] };

        const fiscalQuarterSeriesData = seriesData.reduce((acc, [timestamp, val]) => {
          let year = Moment.utc(timestamp).year();
          const tempMonth = Moment.utc(timestamp).month();
          const fiscalRelativeMonth = (tempMonth + 1 - fiscalYearStartMonth) % 12;
          const absoluteFiscalRelativeMonth = fiscalRelativeMonth >= 0 ? fiscalRelativeMonth : 12 + fiscalRelativeMonth;
          const fiscalQuarter = Math.floor(absoluteFiscalRelativeMonth / 3) + 1;

          if (fiscalRelativeMonth < 0) {
            year -= 1;
          }

          const subYearKey = fiscalYearStartMonth === 1 ? `${year}` : `${year}-${year + 1}`;
          const key = `Q${fiscalQuarter} ${subYearKey}`;
          const newValue = acc[key] ? [...acc[key], val] : [val];

          acc[key] = newValue;

          return acc;
        }, {});

        keys(fiscalQuarterSeriesData).forEach((k) => {
          sumSeriesData[k] = sum(fiscalQuarterSeriesData[k]);
          countSeriesData[k] = fiscalQuarterSeriesData[k].length;
          avgSeriesData[k] = sum(fiscalQuarterSeriesData[k]) / fiscalQuarterSeriesData[k].length;
        });
        averages = values(avgSeriesData).reduce(averageFunc, {
          results: [],
          prev: [],
        });
        sums = values(sumSeriesData).reduce(sumFunc, {
          results: [],
          prev: [],
        });
        xAxis.categories = keys(sumSeriesData);
        aggSumSeriesData = sums.results;
        aggAvgSeriesData = averages.results;

        if (singleTargetAggregationFunction === 'sum') {
          seriesData = zip(keys(sumSeriesData), values(sumSeriesData));
          aggResult = zip(keys(sumSeriesData), aggSumSeriesData);
        } else if (singleTargetAggregationFunction === 'average') {
          seriesData = zip(keys(avgSeriesData), values(avgSeriesData));
          aggResult = zip(keys(avgSeriesData), aggAvgSeriesData);
        }

        seriesData = seriesData.map((d) => Math.round(d[1] * 100) / 100);
        aggResult = aggResult.map((d) => Math.round(d[1] * 100) / 100);
        yAxisMax = max([
          ...seriesData.map((d) => parseFloat(d)),
          ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d))]),
          ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
        ]);
        break;
      case 'fiscalYear':
        xAxis = { categories: [] };
        const fiscalYearSeriesData = seriesData.reduce((acc, [timestamp, val]) => {
          const month = Moment.utc(timestamp).month() + 1;
          let year = Moment.utc(timestamp).year();
          const fiscalRelativeMonth = (month - fiscalYearStartMonth) % 12;

          if (fiscalRelativeMonth < 0) {
            year -= 1;
          }

          const subYearKey = fiscalYearStartMonth === 1 ? `${year}` : `${year}-${year + 1}`;
          const key = `${subYearKey}`;
          const newValue = acc[key] ? [...acc[key], val] : [val];

          acc[key] = newValue;

          return acc;
        }, {});

        keys(fiscalYearSeriesData).forEach((k) => {
          sumSeriesData[k] = sum(fiscalYearSeriesData[k]);
          countSeriesData[k] = fiscalYearSeriesData[k].length;
          avgSeriesData[k] = sum(fiscalYearSeriesData[k]) / fiscalYearSeriesData[k].length;
        });
        averages = values(avgSeriesData).reduce(averageFunc, {
          results: [],
          prev: [],
        });
        sums = values(sumSeriesData).reduce(sumFunc, {
          results: [],
          prev: [],
        });
        xAxis.categories = keys(sumSeriesData);
        aggSumSeriesData = sums.results;
        aggAvgSeriesData = averages.results;

        if (singleTargetAggregationFunction === 'sum') {
          seriesData = zip(keys(sumSeriesData), values(sumSeriesData));
          aggResult = zip(keys(sumSeriesData), aggSumSeriesData);
        } else if (singleTargetAggregationFunction === 'average') {
          seriesData = zip(keys(avgSeriesData), values(avgSeriesData));
          aggResult = zip(keys(avgSeriesData), aggAvgSeriesData);
        }

        seriesData = seriesData.map((d) => Math.round(d[1] * 100) / 100);
        aggResult = aggResult.map((d) => Math.round(d[1] * 100) / 100);
        yAxisMax = max([
          ...seriesData.map((d) => parseFloat(d)),
          ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d))]),
          ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
        ]);
        break;
      case 'calendarQuarter':
        xAxis = { categories: [] };
        seriesData.forEach((d) => {
          const key = Moment.utc(first(d)).format('[Q]Q YYYY');

          sumSeriesData[key] = sumSeriesData[key] ? sumSeriesData[key] + d[1] : d[1];
          countSeriesData[key] = countSeriesData[key] ? countSeriesData[key] + 1 : 1;
        });
        keys(sumSeriesData).forEach((key) => {
          avgSeriesData[key] = sumSeriesData[key] / countSeriesData[key];
        });
        averages = values(avgSeriesData).reduce(averageFunc, {
          results: [],
          prev: [],
        });
        sums = values(sumSeriesData).reduce(sumFunc, {
          results: [],
          prev: [],
        });
        xAxis.categories = keys(sumSeriesData);
        aggSumSeriesData = sums.results;
        aggAvgSeriesData = averages.results;

        if (singleTargetAggregationFunction === 'sum') {
          seriesData = zip(keys(sumSeriesData), values(sumSeriesData));
          aggResult = zip(keys(sumSeriesData), aggSumSeriesData);
        } else if (singleTargetAggregationFunction === 'average') {
          seriesData = zip(keys(avgSeriesData), values(avgSeriesData));
          aggResult = zip(keys(avgSeriesData), aggAvgSeriesData);
        }

        seriesData = seriesData.map((d) => Math.round(d[1] * 100) / 100);
        aggResult = aggResult.map((d) => Math.round(d[1] * 100) / 100);
        yAxisMax = max([
          ...seriesData.map((d) => parseFloat(d)),
          ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d))]),
          ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
        ]);
        break;
      case 'calendarYear':
        xAxis = { categories: [] };
        seriesData.forEach((d) => {
          const key = Moment.utc(first(d)).year();

          sumSeriesData[key] = sumSeriesData[key] ? sumSeriesData[key] + d[1] : d[1];
          countSeriesData[key] = countSeriesData[key] ? countSeriesData[key] + 1 : 1;
        });
        keys(sumSeriesData).forEach((key) => {
          avgSeriesData[key] = sumSeriesData[key] / countSeriesData[key];
        });
        averages = values(avgSeriesData).reduce(averageFunc, {
          results: [],
          prev: [],
        });
        sums = values(sumSeriesData).reduce(sumFunc, {
          results: [],
          prev: [],
        });
        xAxis.categories = keys(sumSeriesData);
        aggSumSeriesData = sums.results;
        aggAvgSeriesData = averages.results;

        if (singleTargetAggregationFunction === 'sum') {
          seriesData = zip(keys(sumSeriesData), values(sumSeriesData));
          aggResult = zip(keys(sumSeriesData), aggSumSeriesData);
        } else if (singleTargetAggregationFunction === 'average') {
          seriesData = zip(keys(avgSeriesData), values(avgSeriesData));
          aggResult = zip(keys(avgSeriesData), aggAvgSeriesData);
        }

        seriesData = seriesData.map((d) => Math.round(d[1] * 100) / 100);
        aggResult = aggResult.map((d) => Math.round(d[1] * 100) / 100);
        yAxisMax = max([
          ...seriesData.map((d) => parseFloat(d)),
          ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d))]),
          ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
        ]);
        break;
      default:
        // set to daily
        break;
    }
  } else {
    // default to calendar year view type
    // xAxis = { categories: [] };
    // seriesData.forEach((d) => {
    //   const key = Moment.utc(first(d)).year();

    //   sumSeriesData[key] = sumSeriesData[key] ? sumSeriesData[key] + d[1] : d[1];
    //   countSeriesData[key] = countSeriesData[key] ? countSeriesData[key] + 1 : 1;
    // });
    // keys(sumSeriesData).forEach((key) => {
    //   avgSeriesData[key] = (sumSeriesData[key]) / countSeriesData[key];
    // });
    // averages = values(avgSeriesData).reduce(averageFunc, { results: [], prev: [] });
    // sums = values(sumSeriesData).reduce(sumFunc, { results: [], prev: [] });
    // xAxis.categories = keys(sumSeriesData);
    // aggSumSeriesData = sums.results;
    // aggAvgSeriesData = averages.results;
    // seriesData = seriesData.map(d => Math.round(d[1] * 100) / 100);
    yAxisMax = max([
      ...seriesData.map((d) => parseFloat(d[1])),
      ...(isUndefined(aggResult) ? [] : [max(aggResult.map((d) => d))]),
      ...(isEmpty(singleTargetTargetAmount) ? [] : [parseFloat(singleTargetTargetAmount)]),
    ]);
  }

  const series = [
    {
      name: 'Actual',
      data: seriesData,
      type: chartType,
      maxPointWidth: 20,
      tooltip: {
        headerFormat: '<span style="font-size: 12px; font-weight: bold; text-decoration: underline;">{point.key}</span><br/>',
        xDateFormat: "%b '%y",
      },
    },
  ];

  if (singleTargetAggregationFunction !== 'none' && !isEmpty(seriesData)) {
    const seriesName = singleTargetAggregationFunction.charAt(0).toUpperCase();
    series.push({
      type: 'spline',
      name: seriesName + singleTargetAggregationFunction.slice(1),
      data: aggResult,
    });
  }

  const yAxis = [
    {
      max: yAxisMax,
      labels: {
        format: yAxisMax >= 1 ? '{value:,.0f}' : '{value:,.2f}',
        style: { color: '#404040' },
      },
      title: {
        text: `${advancedMetricTargetUnit}`,
        style: { color: '#404040' },
      },
      plotLines: [
        {
          color: '#64d06c',
          width: singleTargetTargetAmount ? 2 : 0,
          value: Number(Number(singleTargetTargetAmount).toFixed(2)),
          zIndex: 6,
        },
      ],
    },
  ];
  const csvBtn = showCSVDownload
    ? {
        enabled: true,
        csv: {
          columnHeaderFormatter: null,
          dateFormat: '%Y-%m-%d %H:%M:%S',
          decimalPoint: null,
          itemDelimiter: null,
          lineDelimiter: '\n',
        },
        buttons: {
          contextButton: {
            menuItems: [
              {
                textKey: 'downloadCSV',
                onclick: () => highchartsRef.current.chart.downloadCSV(),
              },
            ],
            ...contextButtonVisualSettings,
          },
        },
      }
    : { enabled: false };
  const options = {
    chart: { height },
    title: {
      text: title,
      style: { display: 'none' },
    },
    credits: false,
    xAxis,
    plotOptions: {
      series: {
        dataLabels: {
          style: {
            fontWeight: 'none',
            fontSize: '9px',
          },
          enabled: true,
          allowOverlap: false,
        },
      },
      column: {
        dataLabels: {
          enabled: true,
          inside: true,
          verticalAlign: 'middle',
        },
      },
    },
    yAxis,
    exporting: csvBtn,
    lang: {
      downloadCSV: 'Download CSV',
      thousandsSep: ',',
      accessibility: {
        chartContainerLabel: title || 'Native Chart',
        userTitle: title || 'Native Chart',
        autoAccessible: false,
        description: title || 'Native Chart',
      },
    },
    caption: title,
    series,
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '20px',
        color: '#404040',
      },
    },
    legend: { enabled: !isEmpty(seriesData) },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} ref={highchartsRef} />;
}

SingleTargetChart.propTypes = {
  height: PropTypes.number,
  measureResults: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  singleTargetAggregationFunction: PropTypes.string.isRequired,
  advancedMetricTargetUnit: PropTypes.string,
  singleTargetMilestoneDate: PropTypes.string,
  singleTargetTargetAmount: PropTypes.string,
  showCSVDownload: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  advancedMetricChartType: PropTypes.string.isRequired,
  viewType: PropTypes.string.isRequired,
  startOfFinancialYear: PropTypes.string.isRequired,
  metricType: PropTypes.string,
  advancedMetric: PropTypes.string,
  advancedMetricType: PropTypes.string,
};
